import { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, Stack,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch,
} from '@mui/material';
import intl from 'components/i18n/ReactIntlWrapper';
import ResizableDrawer from 'components/Layout/ResizableDrawer';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: { name: string; idSkillCategory: number; isSoft: boolean }) => Promise<void>;
  item: any;
  categories: any[];
}

const SkillDrawer: React.FC<Props> = ({ open, onClose, onSave, item, categories }) => {
  const [name, setName] = useState('');
  const [idSkillCategory, setIdSkillCategory] = useState<number | ''>('');
  const [isSoft, setIsSoft] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(item?.name ?? '');
    setIdSkillCategory(item?.idSkillCategory ?? item?.skillCategory?.id ?? '');
    setIsSoft(item?.isSoft ?? false);
  }, [item, open]);

  const handleSave = async () => {
    if (!name.trim() || !idSkillCategory) return;
    setSaving(true);
    try {
      await onSave({ name: name.trim(), idSkillCategory: Number(idSkillCategory), isSoft });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResizableDrawer open={open} onClose={onClose} defaultWidth={400}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        {item ? intl.get('hr.skill.edit') : intl.get('hr.skill.add')}
      </Typography>
      <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <TextField
          label={intl.get('hr.skill.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth required sx={{ mb: 2 }}
        />
        <FormControl fullWidth required sx={{ mb: 2 }}>
          <InputLabel>{intl.get('hr.skill.category')}</InputLabel>
          <Select
            value={idSkillCategory}
            onChange={(e) => setIdSkillCategory(e.target.value as number)}
            label={intl.get('hr.skill.category')}
          >
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={isSoft} onChange={(e) => setIsSoft(e.target.checked)} />}
          label={intl.get('hr.skill.isSoft')}
          sx={{ mb: 3 }}
        />
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={onClose}>{intl.get('common.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {intl.get('common.save')}
          </Button>
        </Stack>
      </Box>
    </ResizableDrawer>
  );
};

export default SkillDrawer;
